import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Siren, MapPin, Phone, Check, BellOff } from "lucide-react";
import { adminApi, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";

const roleLabel = (r) => ({ rider: "Customer", driver: "Driver", restaurant: "Restaurant" }[r] || r);

// Global, always-mounted listener for admins/managers. Polls for new active SOS
// alerts and, on a brand-new one, plays a loud looping alarm + shows a blocking
// modal until acknowledged — so emergencies are impossible to miss.
export default function SosRingListener() {
  const navigate = useNavigate();
  const [queue, setQueue] = useState([]);        // unacknowledged alerts still ringing
  const seenRef = useRef(null);                  // Set of sos_ids already handled
  const audioCtxRef = useRef(null);
  const ringTimerRef = useRef(null);
  const busyRef = useRef(false);

  // Flash the browser tab title while an SOS is ringing, so a backgrounded CRM tab
  // visibly screams for attention even when it can't be force-focused.
  useEffect(() => {
    if (queue.length === 0) return;
    const original = document.title;
    let on = false;
    const t = setInterval(() => {
      on = !on;
      document.title = on ? "🚨 SOS — RESPOND NOW" : original;
    }, 700);
    try { window.focus(); } catch { /* ignore */ }
    return () => { clearInterval(t); document.title = original; };
  }, [queue.length]);

  const beep = useCallback(() => {
    const ctx = audioCtxRef.current;
    if (!ctx) return;
    // Two-tone square-wave chirp — deliberately harsh/loud.
    [880, 1320].forEach((freq, i) => {
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.type = "square";
      o.frequency.value = freq;
      const t = ctx.currentTime + i * 0.22;
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.5, t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.2);
      o.start(t); o.stop(t + 0.22);
    });
  }, []);

  const startRing = useCallback(() => {
    if (ringTimerRef.current) return;
    try {
      if (!audioCtxRef.current) audioCtxRef.current = new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtxRef.current.state === "suspended") audioCtxRef.current.resume();
    } catch { /* audio unavailable */ }
    beep();
    ringTimerRef.current = setInterval(beep, 900);
  }, [beep]);

  const stopRing = useCallback(() => {
    if (ringTimerRef.current) { clearInterval(ringTimerRef.current); ringTimerRef.current = null; }
  }, []);

  // Desktop/browser notification so staff see an SOS even when the CRM tab is in the background.
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission().catch(() => {});
    }
  }, []);
  const notifyDesktop = useCallback((fresh) => {
    try { window.focus(); } catch { /* ignore */ }  // best-effort: bring the CRM to the front
    if (!("Notification" in window) || Notification.permission !== "granted") return;
    fresh.forEach((a) => {
      try {
        const n = new Notification("🚨 Emergency SOS", {
          body: `${a.user_name} (${roleLabel(a.user_role)}) needs help. Open the SOS panel to respond.`,
          tag: a.sos_id, requireInteraction: true,
        });
        n.onclick = () => { window.focus(); navigate("/app/admin/sos"); n.close(); };
      } catch { /* ignore */ }
    });
  }, [navigate]);

  // Poll for active alerts every 8s.
  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const d = await adminApi.sosList("active");
        if (!alive) return;
        const alerts = d.alerts || [];
        if (seenRef.current === null) {
          // First run after login/refresh: remember existing alerts, don't blast for old ones.
          seenRef.current = new Set(alerts.map((a) => a.sos_id));
          return;
        }
        const fresh = alerts.filter((a) => !seenRef.current.has(a.sos_id));
        if (fresh.length) {
          fresh.forEach((a) => seenRef.current.add(a.sos_id));
          setQueue((q) => [...q, ...fresh]);
          notifyDesktop(fresh);
        }
      } catch { /* ignore transient */ }
    };
    poll();
    const t = setInterval(poll, 8000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // Ring while there is something in the queue.
  useEffect(() => {
    if (queue.length > 0) startRing(); else stopRing();
    return () => {};
  }, [queue.length, startRing, stopRing]);

  useEffect(() => () => stopRing(), [stopRing]);

  const current = queue[0];
  const dismiss = () => setQueue((q) => q.slice(1));

  const acknowledge = async () => {
    if (!current || busyRef.current) return;
    busyRef.current = true;
    try { await adminApi.ackSos(current.sos_id); } catch (e) { toast.error(errorMessage(e)); }
    busyRef.current = false;
    dismiss();
  };

  const viewAll = () => { dismiss(); navigate("/app/admin/sos"); };

  if (!current) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/60 p-4" data-testid="sos-ring-modal">
      <div className="w-full max-w-md overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center gap-3 bg-red-600 px-6 py-4 text-white">
          <Siren className="h-7 w-7 animate-pulse" />
          <div>
            <p className="text-lg font-extrabold leading-tight">Emergency SOS</p>
            <p className="text-xs opacity-90">{queue.length > 1 ? `${queue.length} active alerts` : "Immediate response needed"}</p>
          </div>
        </div>
        <div className="space-y-2 px-6 py-5">
          <p className="text-base font-semibold text-ink">{current.user_name} <span className="rounded-full bg-surface-100 px-2 py-0.5 text-xs font-medium text-ink-soft">{roleLabel(current.user_role)}</span></p>
          {current.user_phone && <p className="text-sm text-ink-soft"><Phone className="mb-0.5 mr-1 inline h-3 w-3" /> {current.user_phone}</p>}
          {current.context_label && <p className="text-sm text-ink-soft"><span className="text-ink-muted">On:</span> {current.context_label}</p>}
          {current.note && <p className="rounded-xl bg-surface-100 p-3 text-sm text-ink-soft">{current.note}</p>}
        </div>
        <div className="grid grid-cols-2 gap-2 px-6 pb-6">
          <a href={current.map_url} target="_blank" rel="noreferrer" className="col-span-2 inline-flex items-center justify-center gap-2 rounded-full bg-red-600 px-4 py-3 text-sm font-semibold text-white hover:bg-red-700" data-testid="sos-ring-map"><MapPin className="h-4 w-4" /> Open location</a>
          {current.user_phone && <a href={`tel:${current.user_phone}`} className="inline-flex items-center justify-center gap-2 rounded-full border border-surface-200 px-4 py-3 text-sm font-semibold text-ink-soft hover:bg-surface-100" data-testid="sos-ring-call"><Phone className="h-4 w-4" /> Call</a>}
          <Button onClick={acknowledge} className={`rounded-full bg-ink text-white hover:bg-ink/90 ${current.user_phone ? "" : "col-span-2"}`} data-testid="sos-ring-ack"><Check className="mr-2 h-4 w-4" /> Acknowledge</Button>
          <button type="button" onClick={viewAll} className="col-span-2 inline-flex items-center justify-center gap-2 text-xs font-semibold text-ink-muted" data-testid="sos-ring-viewall"><BellOff className="h-3 w-3" /> Silence & open SOS panel</button>
        </div>
      </div>
    </div>
  );
}
